import React, { useRef, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import Badge from "react-bootstrap/Badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faDatabase,
    faDownload,
    faUpload,
    faFileCode,
    faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";

import { AppLayout } from "../components/appLayout";
import useApiClient from "../hooks/useApiClient";
import { getErrorMessage } from "../helpers/errorHelper";

export const Backup = () => {
    const apiClient = useApiClient();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const onBackup = async () => {
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const backup = await apiClient!.backup();
            const jsonData = typeof backup?.toJSON === 'function' ? backup.toJSON() : backup;
            const file = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = url;
            link.download = `videolibrary-backup-${new Date().toISOString()}.json`;
            link.click();
            URL.revokeObjectURL(url);
            setSuccessMessage("Backup downloaded successfully.");
        } catch (err) {
            console.error(err);
            setError(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const onRestore = () => {
        setError(null);
        setSuccessMessage(null);
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setSelectedFileName(file.name);
        console.log(file);
    };

    return (
        <AppLayout>
            <Container className="py-2">
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                        <h1 className="mb-1">Backup</h1>
                        <p className="text-body-secondary mb-0">
                            Export your video library data as JSON or restore from a previous backup.
                        </p>
                    </div>
                </div>

                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4">
                        {error}
                    </Alert>
                )}

                {successMessage && (
                    <Alert variant="success" onClose={() => setSuccessMessage(null)} dismissible className="mb-4">
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                        {successMessage}
                    </Alert>
                )}

                <Row className="g-4">
                    <Col md={6}>
                        <Card className="h-100">
                            <Card.Body className="d-flex flex-column p-4">
                                <div className="d-flex align-items-center gap-3 mb-3">
                                    <div
                                        className="d-inline-flex align-items-center justify-content-center rounded-3 bg-primary-subtle text-primary"
                                        style={{ width: 44, height: 44 }}
                                    >
                                        <FontAwesomeIcon icon={faDatabase} size="lg" />
                                    </div>
                                    <div>
                                        <h2 className="h5 mb-0">Export Library</h2>
                                        <small className="text-body-secondary">Download a full JSON snapshot</small>
                                    </div>
                                </div>
                                <p className="text-body-secondary flex-grow-1 mb-4">
                                    Create a complete backup file containing all of your videos, bookmarks, categories, and tags.
                                </p>
                                <div>
                                    <Button variant="primary" onClick={onBackup} disabled={loading}>
                                        {loading ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                                Backing up...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faDownload} className="me-2" />
                                                Backup
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={6}>
                        <Card className="h-100">
                            <Card.Body className="d-flex flex-column p-4">
                                <div className="d-flex align-items-center gap-3 mb-3">
                                    <div
                                        className="d-inline-flex align-items-center justify-content-center rounded-3 bg-secondary-subtle text-secondary-emphasis"
                                        style={{ width: 44, height: 44 }}
                                    >
                                        <FontAwesomeIcon icon={faFileCode} size="lg" />
                                    </div>
                                    <div>
                                        <h2 className="h5 mb-0">Restore Library</h2>
                                        <small className="text-body-secondary">Import from a backup JSON file</small>
                                    </div>
                                </div>
                                <p className="text-body-secondary flex-grow-1 mb-4">
                                    Select a previously exported <code>.json</code> backup file from your device to restore your library data.
                                </p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".json"
                                    onChange={handleFileChange}
                                    className="d-none"
                                    data-testid="restore-file-input"
                                />
                                <div className="d-flex align-items-center gap-3 flex-wrap">
                                    <Button variant="secondary" onClick={onRestore} disabled={loading}>
                                        <FontAwesomeIcon icon={faUpload} className="me-2" />
                                        Restore
                                    </Button>
                                    {selectedFileName && (
                                        <Badge bg="secondary-subtle" className="text-secondary-emphasis fw-normal px-3 py-2">
                                            {selectedFileName}
                                        </Badge>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </AppLayout>
    );
};